import moment from 'moment';
import type { AuditReportPackScopeMetric } from 'services/auditService';
import {
  getCurrentInventoryPackScopeKey,
  type CurrentInventoryPackScopeItem,
} from './currentInventoryPackScope';

export type CurrentInventoryPackScopeQueryParams = {
  metric: AuditReportPackScopeMetric;
  report_date?: string;
  start_date?: string;
  end_date?: string;
  audit_id?: string;
};

const VALID_METRICS: AuditReportPackScopeMetric[] = [
  'opening',
  'activated',
  'closing',
  'moved_to_backstock',
  'counter_sold_out',
  'backstock_opening',
  'backstock_activated_to_backstock',
  'backstock_moved_from_counter',
  'backstock_returned_to_counter',
  'backstock_returned_to_lottery',
  'backstock_inventory_depletion',
  'backstock_closing',
  'vending_opening',
  'vending_activated',
  'vending_moved',
  'vending_sold_out',
  'vending_closing',
];

const METRIC_LABELS: Record<AuditReportPackScopeMetric, string> = {
  opening: 'Opening Packs',
  activated: 'Activated Packs',
  closing: 'Closing Packs',
  moved_to_backstock: 'Moved to Backstock',
  counter_sold_out: 'Counter Sold Out Packs',
  backstock_opening: 'Backstock Opening Packs',
  backstock_activated_to_backstock: 'Activated to Backstock',
  backstock_moved_from_counter: 'Counter to Backstock',
  backstock_returned_to_counter: 'Backstock to Counter',
  backstock_returned_to_lottery: 'Returned to Lottery',
  backstock_inventory_depletion: 'Sold Out from Backstock',
  backstock_closing: 'Backstock Closing Packs',
  vending_opening: 'Vending Opening Packs',
  vending_activated: 'Vending Activated Packs',
  vending_moved: 'Vending Moved Packs',
  vending_sold_out: 'Vending Sold Out Packs',
  vending_closing: 'Vending Closing Packs',
};

const isValidMetric = (value: string | null): value is AuditReportPackScopeMetric =>
  Boolean(value) && VALID_METRICS.includes(value as AuditReportPackScopeMetric);

const formatDate = (value: string | null | undefined) => {
  if (!value) {
    return null;
  }

  const formatted = moment(value).format('MM/DD/YYYY');
  return formatted === 'Invalid date' ? null : formatted;
};

const buildDateSuffix = (params: CurrentInventoryPackScopeQueryParams) => {
  if (params.report_date) {
    const reportDate = formatDate(params.report_date);
    return reportDate ? ` on ${reportDate}` : '';
  }

  if (params.start_date && params.end_date) {
    const startDate = formatDate(params.start_date);
    const endDate = formatDate(params.end_date);
    return startDate && endDate ? ` ${startDate} - ${endDate}` : '';
  }

  if (params.audit_id) {
    return ' on Selected Audit';
  }

  return '';
};

const hasDateRange = (params: CurrentInventoryPackScopeQueryParams) =>
  Boolean(params.start_date && params.end_date);

export const hasRequiredCurrentInventoryPackScopeParams = (
  params: CurrentInventoryPackScopeQueryParams
) => {
  switch (params.metric) {
    case 'opening':
    case 'activated':
    case 'closing':
    case 'counter_sold_out':
      return Boolean(params.report_date || hasDateRange(params));
    case 'backstock_opening':
    case 'backstock_activated_to_backstock':
    case 'backstock_moved_from_counter':
    case 'backstock_returned_to_counter':
    case 'backstock_returned_to_lottery':
    case 'backstock_inventory_depletion':
    case 'backstock_closing':
    case 'vending_opening':
    case 'vending_activated':
    case 'vending_moved':
    case 'vending_sold_out':
    case 'vending_closing':
      return Boolean(params.report_date || hasDateRange(params));
    case 'moved_to_backstock':
      return Boolean(params.report_date || params.audit_id || hasDateRange(params));
    default:
      return false;
  }
};

export const parseCurrentInventoryPackScopeQuery = (
  searchParams: URLSearchParams
): CurrentInventoryPackScopeQueryParams | null => {
  const metric = searchParams.get('metric');
  if (!isValidMetric(metric)) {
    return null;
  }

  const params: CurrentInventoryPackScopeQueryParams = {
    metric,
  };

  const reportDate = searchParams.get('report_date');
  if (reportDate) {
    params.report_date = reportDate;
  }

  const startDate = searchParams.get('start_date');
  if (startDate) {
    params.start_date = startDate;
  }

  const endDate = searchParams.get('end_date');
  if (endDate) {
    params.end_date = endDate;
  }

  const auditId = searchParams.get('audit_id');
  if (auditId) {
    params.audit_id = auditId;
  }

  return params;
};

export const hasCurrentInventoryPackScopeQuery = (
  searchParams: URLSearchParams
) => Boolean(parseCurrentInventoryPackScopeQuery(searchParams));

export const buildCurrentInventoryPackScopeTitle = (params: CurrentInventoryPackScopeQueryParams) => {
  const baseLabel = METRIC_LABELS[params.metric];
  return `${baseLabel}${buildDateSuffix(params)}`;
};

export const getCurrentInventoryPackScopeKeySet = (packs: CurrentInventoryPackScopeItem[] = []) =>
  new Set(packs.map((pack) => getCurrentInventoryPackScopeKey(pack.game_id, pack.pack_id)));
